// Classroom watch progress — device-local by design (no account needed to
// learn, matching the site pledge). localStorage keyed per lesson id; the
// UI copy says "saved on this device" so nobody expects cross-device sync.
import { useCallback, useSyncExternalStore } from "react";

const KEY = "ahp_classroom_progress_v1";

export interface LessonProgress {
  pos: number;          // last playback position, seconds
  dur: number;          // duration observed at save time
  pct: number;          // 0..100 furthest-watched percentage
  completedAt?: string; // ISO date when >=90% watched
  updatedAt: string;
}

type Store = Record<string, LessonProgress>;

let cache: Store | null = null;
const listeners = new Set<() => void>();

function read(): Store {
  if (cache) return cache;
  try {
    cache = JSON.parse(localStorage.getItem(KEY) || "{}");
  } catch {
    cache = {};
  }
  return cache!;
}

function write(next: Store) {
  cache = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode / quota — progress just doesn't persist */
  }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useClassroomProgress() {
  const store = useSyncExternalStore(subscribe, read, read);

  const savePosition = useCallback((id: string, pos: number, dur: number) => {
    const prev = read()[id];
    const pct = dur > 0 ? Math.max(prev?.pct ?? 0, Math.min(100, (pos / dur) * 100)) : 0;
    const entry: LessonProgress = {
      pos,
      dur,
      pct,
      completedAt: pct >= 90 ? prev?.completedAt ?? new Date().toISOString() : prev?.completedAt,
      updatedAt: new Date().toISOString(),
    };
    write({ ...read(), [id]: entry });
  }, []);

  const markComplete = useCallback((id: string) => {
    const prev = read()[id];
    write({
      ...read(),
      [id]: {
        pos: 0,
        dur: prev?.dur ?? 0,
        pct: 100,
        completedAt: prev?.completedAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    });
  }, []);

  return { progress: store, savePosition, markComplete };
}

/** Most recently touched, unfinished lesson id (for the Continue Watching hero). */
export function continueWatchingId(store: Store): string | null {
  const rows = Object.entries(store)
    .filter(([, p]) => p.pct > 2 && p.pct < 90)
    .sort((a, b) => (b[1].updatedAt > a[1].updatedAt ? 1 : -1));
  return rows[0]?.[0] ?? null;
}
